
import React, { useState, useEffect, useMemo } from 'react';
import { Ticket, AppSettings, RepairChecklist, PaymentStatus, RepairStageStatus, TicketStatus, PreCompletionCheckData } from '../types';
import { Button } from './Button';

interface PreCompletionCheckModalProps {
  isOpen: boolean;
  onClose: () => void;
  data: PreCompletionCheckData | null;
  onProceed: (updatedData: PreCompletionCheckData) => void;
  onSaveNotesAndClose: (ticketId: string, notes: string) => void;
  appSettings: AppSettings;
  repairChecklists: RepairChecklist[];
}

// Icons
const CheckCircleIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5 mr-2 text-emerald-500" {...props}><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z" clipRule="evenodd" /></svg>
);
const ExclamationTriangleIconSolid: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5 mr-2 text-amber-600" {...props}><path fillRule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" /></svg>
);
const XCircleIconSolid: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5 mr-2 text-red-600" {...props}><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z" clipRule="evenodd" /></svg>
);
const SparklesIconMini: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 20 20" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 mr-1.5 text-purple-500" {...props}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L1.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.25 12L17 14.187l-1.25-2.187a3.375 3.375 0 00-2.682-2.682L10.813 9l2.187-1.25a3.375 3.375 0 002.682-2.682L17 1.813l1.25 2.187a3.375 3.375 0 002.682 2.682L23.187 9l-2.187 1.25a3.375 3.375 0 00-2.682 2.682z" transform="scale(0.83) translate(2,2)" />
  </svg>
);
const LoadingSpinnerMini: React.FC = () => (
  <div className="flex items-center space-x-1">
    <div className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-pulse delay-0"></div>
    <div className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-pulse delay-100"></div>
    <div className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-pulse delay-200"></div>
  </div>
);


export const PreCompletionCheckModal: React.FC<PreCompletionCheckModalProps> = ({
  isOpen,
  onClose,
  data,
  onProceed,
  onSaveNotesAndClose,
  appSettings,
  repairChecklists,
}) => {
  const [internalCheckNotes, setInternalCheckNotes] = useState('');
  const [sendSms, setSendSms] = useState(data?.targetSendSms || false);
  const [sendCompletionEmail, setSendCompletionEmail] = useState(data?.targetSendCompletionEmail || false);
  const [deviceCleaned, setDeviceCleaned] = useState(false);

  useEffect(() => {
    if (data) {
      setSendSms(data.targetSendSms || false);
      setSendCompletionEmail(data.targetSendCompletionEmail || false);
    }
    // Reset device cleaned state when modal opens for a new ticket/check
    setDeviceCleaned(false);
    setInternalCheckNotes('');
  }, [data, isOpen]); // Ensure reset when data changes or modal re-opens

  const [aiSanityState, setAiSanityState] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [aiSanityMessage, setAiSanityMessage] = useState<string | null>(null);


  useEffect(() => {
    if (isOpen && data) {
      const { ticket } = data;
      const currentChecklist = repairChecklists.find(cl => cl.id === ticket.activeChecklistId);

      setAiSanityState('loading');
      setAiSanityMessage(null);
      const timer = setTimeout(() => {
        const issues: string[] = [];
        if (ticket.activeChecklistId && currentChecklist) {
          const incompleteRequired = currentChecklist.items.filter(
            item => item.text.toLowerCase().includes('liquid damage') &&
                    !(ticket.checklistProgress?.[item.id])
          );
          if (incompleteRequired.length > 0 && ticket.internalNotes.some(n => n.text.toLowerCase().includes('liquid'))) {
            issues.push("AI Suggestion: Notes mention 'liquid damage', but a related checklist item like 'Liquid Damage Indicator Check' might not be marked. Please verify.");
          }
        }
        if (ticket.partsUsed.length === 0 && ticket.subject.toLowerCase().includes('replace')) {
          issues.push("AI Suggestion: Repair subject suggests replacement, but no parts are logged. Please verify.");
        }

        if (issues.length > 0) {
          setAiSanityMessage(issues.join('\n'));
          setAiSanityState('error');
        } else {
          setAiSanityMessage("✅ AI finds no obvious discrepancies based on simple checks.");
          setAiSanityState('success');
        }
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [isOpen, data, repairChecklists]);


  if (!isOpen || !data) return null;

  const { ticket, targetStatus } = data;

  const currentChecklist = repairChecklists.find(cl => cl.id === ticket.activeChecklistId);
  const totalChecklistItems = currentChecklist?.items.length || 0;
  const completedChecklistItems = totalChecklistItems > 0
    ? currentChecklist!.items.filter(item => ticket.checklistProgress?.[item.id] === true).length
    : 0;
  const allChecklistItemsCompleted = totalChecklistItems > 0 ? completedChecklistItems === totalChecklistItems : true;


  const isPaid = ticket.paymentStatus === PaymentStatus.PAID || ticket.amountDue === 0;
  const outstandingAmount = ticket.amountDue > 0 && ticket.paymentStatus !== PaymentStatus.PAID ? ticket.amountDue : 0;

  const allChecksPassStrict = allChecklistItemsCompleted && isPaid && deviceCleaned;

  const handleProceedAnyway = () => {
    const updatedDataWithNotifications: PreCompletionCheckData = {
      ...data,
      targetSendSms: sendSms,
      targetSendCompletionEmail: sendCompletionEmail,
    };
    onProceed(updatedDataWithNotifications);
  };

  const handleSaveNotesAndCloseModal = () => {
    onSaveNotesAndClose(ticket.id, internalCheckNotes);
  };

  const renderChecklistItem = (
    title: string,
    status: 'ok' | 'warn' | 'fail',
    details: string | React.ReactNode,
    action?: React.ReactNode
  ) => {
    let IconComponent;
    let iconColorClass = '';
    let bgColorClass = 'bg-slate-50';

    switch (status) {
      case 'ok':
        IconComponent = CheckCircleIcon;
        iconColorClass = 'text-emerald-500';
        bgColorClass = 'bg-emerald-50 border-emerald-200';
        break;
      case 'warn':
        IconComponent = ExclamationTriangleIconSolid;
        iconColorClass = 'text-amber-600';
        bgColorClass = 'bg-amber-50 border-amber-200';
        break;
      case 'fail':
        IconComponent = XCircleIconSolid;
        iconColorClass = 'text-red-600';
        bgColorClass = 'bg-red-50 border-red-200';
        break;
    }

    return (
      <div className={`p-3 border rounded-md ${bgColorClass} flex items-start`}>
        <IconComponent className={`w-5 h-5 mr-2 flex-shrink-0 ${iconColorClass}`} />
        <div className="flex-grow">
          <h4 className="text-sm font-semibold text-slate-700">{title}</h4>
          <div className="text-xs text-slate-600">{details}</div>
          {action && <div className="mt-1">{action}</div>}
        </div>
      </div>
    );
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center p-4 z-[150]"
      aria-labelledby="pre-completion-modal-title"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div
        className="bg-white p-6 rounded-lg shadow-xl w-full max-w-xl max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center mb-4 border-b pb-3">
          <h2 id="pre-completion-modal-title" className="text-lg font-semibold text-slate-800">
            Final Check for Ticket: <span className="text-[var(--color-primary)]">{ticket.id}</span>
          </h2>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-700" aria-label="Close modal">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="flex-grow overflow-y-auto pr-2 space-y-3">
          <div className="text-xs text-slate-600 bg-slate-50 p-2 rounded border">
            <strong>Customer:</strong> {ticket.customer} <br />
            <strong>Device:</strong> {ticket.deviceModel || 'N/A'} - {ticket.subject} <br />
            <strong>Target Status:</strong> {targetStatus}
          </div>

          {renderChecklistItem(
            "Payment Status",
            isPaid ? 'ok' : 'fail',
            isPaid ? `Paid in Full ($${ticket.amountDue.toFixed(2)})` : `Outstanding: $${outstandingAmount.toFixed(2)}`,
            !isPaid ? <Button variant="secondary" className="text-xs !px-2 !py-1" onClick={() => alert("Navigate to payment - TBD")}>Go to Payment</Button> : undefined
          )}

          {renderChecklistItem(
            "Repair Checklist",
            allChecklistItemsCompleted ? 'ok' : 'warn',
            currentChecklist
              ? `${completedChecklistItems} of ${totalChecklistItems} items completed on "${currentChecklist.title}".`
              : "No active checklist for this ticket.",
            currentChecklist && !allChecklistItemsCompleted ? <Button variant="ghost" className="text-xs !px-2 !py-1" onClick={() => alert("Navigate to checklist tab - TBD")}>View Checklist</Button> : undefined
          )}

          {renderChecklistItem(
            "Parts Logged",
            ticket.partsUsed.length > 0 ? 'ok' : 'warn',
            `${ticket.partsUsed.length} part(s) logged for this repair.`,
            ticket.partsUsed.length === 0 ? <span className="text-slate-500 italic">Consider if parts should be logged.</span> : undefined
          )}

          <div className={`p-3 border rounded-md ${deviceCleaned ? 'bg-emerald-50 border-emerald-200' : 'bg-slate-50 border-slate-200'}`}>
            <div className="flex items-center">
                <input
                    type="checkbox"
                    id={`fqc-cleaned-${ticket.id}`}
                    checked={deviceCleaned}
                    onChange={(e) => setDeviceCleaned(e.target.checked)}
                    className="h-4 w-4 text-[var(--color-primary)] border-slate-300 rounded focus:ring-[var(--color-primary)]"
                />
                <label htmlFor={`fqc-cleaned-${ticket.id}`} className="ml-2 text-sm font-semibold text-slate-700">
                    Device cleaned and sanitized?
                </label>
            </div>
             {!deviceCleaned && <p className="text-xs text-amber-700 ml-6 mt-0.5">Required before completion.</p>}
          </div>

           <div className="p-3 border border-slate-200 rounded-md bg-slate-50">
                <h4 className="text-sm font-semibold text-slate-700 mb-1 flex items-center"><SparklesIconMini /> AI Sanity Check:</h4>
                {aiSanityState === 'loading' && <div className="text-xs text-slate-500 flex items-center"><LoadingSpinnerMini /><span className="ml-1">Analyzing...</span></div>}
                {aiSanityState === 'error' && aiSanityMessage && <p className="text-xs text-amber-700">{aiSanityMessage}</p>}
                {aiSanityState === 'success' && aiSanityMessage && <p className="text-xs text-emerald-700">{aiSanityMessage}</p>}
           </div>


          <div className="border-t pt-3 mt-3">
            <label htmlFor="preCheckNotes" className="block text-xs font-medium text-slate-600 mb-1">Pre-Check Notes (Optional, for internal log):</label>
            <textarea
              id="preCheckNotes"
              value={internalCheckNotes}
              onChange={(e) => setInternalCheckNotes(e.target.value)}
              rows={2}
              className="w-full p-1.5 border border-slate-300 rounded-md text-xs focus:ring-1 focus:ring-[var(--color-primary)] focus:border-[var(--color-primary)]"
              placeholder="e.g., Customer approved proceeding despite X..."
            />
          </div>

          {(targetStatus === TicketStatus.READY_FOR_PICKUP || targetStatus === TicketStatus.COMPLETED) && (
            <div className="pt-2 space-y-1">
                {targetStatus === TicketStatus.READY_FOR_PICKUP && (
                     <div className="flex items-center">
                        <input type="checkbox" id={`precheck-sms-${ticket.id}`} checked={sendSms} onChange={(e) => setSendSms(e.target.checked)} className="h-4 w-4 text-[var(--color-primary)] border-slate-300 rounded focus:ring-[var(--color-primary)]" />
                        <label htmlFor={`precheck-sms-${ticket.id}`} className="ml-2 text-xs text-slate-600">Send 'Ready for Pickup' SMS to customer?</label>
                    </div>
                )}
                 {targetStatus === TicketStatus.COMPLETED && (
                     <div className="flex items-center">
                        <input type="checkbox" id={`precheck-email-${ticket.id}`} checked={sendCompletionEmail} onChange={(e) => setSendCompletionEmail(e.target.checked)} className="h-4 w-4 text-[var(--color-primary)] border-slate-300 rounded focus:ring-[var(--color-primary)]" />
                        <label htmlFor={`precheck-email-${ticket.id}`} className="ml-2 text-xs text-slate-600">Queue 'Service Summary' Email to customer?</label>
                    </div>
                )}
            </div>
          )}

        </div>

        <div className="border-t pt-4 mt-4 flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-3">
          <Button variant="ghost" onClick={handleSaveNotesAndCloseModal} className="text-sm">
            Save Notes & Review Later
          </Button>
          <Button
            variant={allChecksPassStrict ? "primary" : "danger"}
            onClick={handleProceedAnyway}
            className="text-sm"
          >
            {allChecksPassStrict ? `Proceed to ${targetStatus}` : `Proceed Anyway to ${targetStatus}`}
          </Button>
        </div>
        {!allChecksPassStrict && (
             <p className="text-xs text-red-600 mt-2 text-right">Warning: Not all checks passed. Proceed with caution.</p>
        )}
      </div>
    </div>
  );
};
